import type { MathPeriod, MathRow } from './mathReportApi';

/** Сколько строк показывать на странице по умолчанию и какие размеры страницы доступны */
export const DEFAULT_PAGE_SIZE = 100;
export const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

/** Допустимый ввод в ячейке: число со знаком, разделитель - точка или запятая */
export const CELL_INPUT_PATTERN = /^-?\d*[.,]?\d*$/;

export interface MonthOption {
  value: string;
  label: string;
}

function formatMonthValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Текущий месяц в виде «2026-10» */
export function getCurrentMonth(): string {
  return formatMonthValue(new Date());
}

/** Текущий и 11 предыдущих месяцев, от свежего к старому */
export function getMonthsList(): MonthOption[] {
  const now = new Date();
  const months: MonthOption[] = [];
  for (let i = 0; i < 12; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ value: formatMonthValue(date), label: date.toLocaleString('ru', { year: 'numeric', month: 'long' }) });
  }
  return months;
}

/** «2026-10» -> { month: 10, year: 2026 } */
export function parseMonth(value: string): MathPeriod {
  const parts = value.split('-');
  return { month: Number(parts[1]), year: Number(parts[0]) };
}

export type ColumnKind = 'text' | 'status' | 'audit' | 'number';

/** Как показывать колонку: логин аптеки - текстом, статус и ревизия - особо, остальное - суммы */
export function columnKind(field: string): ColumnKind {
  if (field === 'pharmacyNum') return 'text';
  if (field === 'checkStatus') return 'status';
  if (field === 'audit') return 'audit';
  return 'number';
}

/** Число из значения ячейки; null - пусто или не число */
export function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return isFinite(parsed) ? parsed : null;
  }
  return null;
}

// Один форматтер на все ячейки: toLocaleString с параметрами создавал бы его заново на каждой
const MONEY_FORMAT = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Сумма с двумя знаками; не число показывается как есть, пусто - пустой строкой */
export function formatMoney(value: unknown): string {
  const parsed = toNumber(value);
  if (parsed !== null) return MONEY_FORMAT.format(parsed);
  return value === null || value === undefined ? '' : String(value);
}

/** Ревизия проведена, а излишка и недостачи нет - такие строки подсвечиваются на проверку */
export function isAuditClean(row: MathRow): boolean {
  return toNumber(row.audit) === 1 && !toNumber(row.overage) && !toNumber(row.shortfall);
}

/** Поиск по номеру (логину) аптеки */
export function filterRows(rows: MathRow[], query: string): MathRow[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) => String(row.pharmacyNum || '').toLowerCase().indexOf(needle) >= 0);
}

/** Текст для поля ввода: десятичная запятая, как в остальных формах отчетности */
export function toEditText(value: number | null): string {
  return value === null ? '' : String(value).replace('.', ',');
}

/** Введенное число; null - ввод не является числом ("-", "."). Пустой ввод проверяется до вызова */
export function parseCellInput(text: string): number | null {
  const parsed = Number(text.replace(/\s/g, '').replace(',', '.'));
  return isFinite(parsed) ? parsed : null;
}
